import { useState, useEffect, useRef } from 'react';
import noop from 'utils/noop';
import { ENTRANCE_TIME, STANDARD_TIME } from 'constants/animations';
import { useHeightAnimation } from 'utils/hooks/useHeightAnimation';
import type { VariantToastPropsType, ToastPropsType } from './types';

import {
  ToastWrapper,
  ContentWrapper,
  ContentSection,
  TextSection,
  ActionsSection,
  ActionWrapper,
  Action,
  CloseIcon,
  CloseButton,
  PositiveIcon,
  NegativeIcon,
  WarningIcon,
  InfoIcon,
} from './Toast.styles';
import { TOAST_VARIANTS, VISIBLE_ENUM } from './constants';

export const BasicToast = ({
  children,
  Icon,
  variant = TOAST_VARIANTS.POSITIVE,
  actions = [],
  closeToast = noop,
  timeout = 6000,
  noDelay = false,
  ...props
}: ToastPropsType) => {
  const [visibleState, setVisibleState] = useState(VISIBLE_ENUM.ENTERING);
  const [toastTimeout, setToastTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const toastRef = useRef<HTMLDivElement>(null);

  /**
   * handles click of close button or timeout
   * if toast has not exited already (timeout after it has exited) exit
   */
  const handleClose = () => {
    const { collapse } = useHeightAnimation(toastRef.current);
    if (visibleState !== VISIBLE_ENUM.EXITING) {
      collapse(0, true);
      setVisibleState(VISIBLE_ENUM.EXITING);
      // This deletes toast after exit animation
      setTimeout(closeToast, STANDARD_TIME * 1750);
    }
  };

  /**
   * Creates a timeout to hide the toast.
   */
  const createTimeout = () => {
    if (!noDelay && timeout > 0) {
      setToastTimeout(setTimeout(handleClose, timeout));
    }
  };

  /**
   * Clears the timeout used to hide the toast.
   */
  const stopTimeout = () => {
    if (toastTimeout) {
      clearTimeout(toastTimeout);
      setToastTimeout(null);
    }
  };

  /**
   * Closes toast after mounting
   */
  useEffect(() => {
    // sets visible variable so the entrance animation stops
    setTimeout(() => setVisibleState(VISIBLE_ENUM.VISIBLE), ENTRANCE_TIME * 1000);
    createTimeout();
  }, []);

  return (
    <ToastWrapper
      data-test="toast"
      onMouseEnter={stopTimeout}
      onMouseOver={stopTimeout}
      onMouseLeave={createTimeout}
      $visibleState={visibleState}
      ref={toastRef}
      {...props}
    >
      <ContentWrapper $variant={variant}>
        {Icon}
        <ContentSection>
          <TextSection>{children}</TextSection>
          {actions.length > 0 && (
            <ActionsSection>
              {actions.map(({ title, href, externalAction, onClick }) => {
                const externalActionProps = externalAction && {
                  target: '_blank',
                  rel: 'noopener',
                };
                return (
                  <ActionWrapper key={title}>
                    <Action href={href} onClick={onClick} {...externalActionProps}>
                      {title}
                    </Action>
                  </ActionWrapper>
                );
              })}
            </ActionsSection>
          )}
        </ContentSection>
        <CloseButton
          data-test="toast-close-button"
          aria-label="Close Button"
          onClick={handleClose}
          onMouseOver={stopTimeout}
          onMouseEnter={stopTimeout}
        >
          <CloseIcon />
        </CloseButton>
      </ContentWrapper>
    </ToastWrapper>
  );
};

export const PositiveToast = ({ timeout = 5000, ...props }: VariantToastPropsType) => (
  <BasicToast
    data-test="positive-toast"
    variant={TOAST_VARIANTS.POSITIVE}
    Icon={<PositiveIcon data-test="positive-icon" />}
    timeout={timeout}
    {...props}
  />
);
export const WarningToast = ({ timeout = 5000, ...props }: VariantToastPropsType) => (
  <BasicToast
    data-test="warning-toast"
    variant={TOAST_VARIANTS.WARNING}
    Icon={<WarningIcon data-test="warning-icon" />}
    timeout={timeout}
    {...props}
  />
);

export const NegativeToast = ({ timeout = 10000, ...props }: VariantToastPropsType) => (
  <BasicToast
    data-test="negative-toast"
    variant={TOAST_VARIANTS.NEGATIVE}
    Icon={<NegativeIcon data-test="negative-icon" />}
    timeout={timeout}
    {...props}
  />
);

export const InfoToast = ({ timeout = 10000, ...props }: VariantToastPropsType) => (
  <BasicToast
    data-test="info-toast"
    variant={TOAST_VARIANTS.INFO}
    Icon={<InfoIcon data-test="info-icon" />}
    timeout={timeout}
    {...props}
  />
);

export const Toast = ({ variant, ...props }: ToastPropsType) => {
  switch (variant) {
    case TOAST_VARIANTS.NEGATIVE:
      return <NegativeToast {...props} />;
    case TOAST_VARIANTS.WARNING:
      return <WarningToast {...props} />;
    case TOAST_VARIANTS.INFO:
      return <InfoToast {...props} />;
    default:
      return <PositiveToast {...props} />;
  }
};

export default Toast;
